<template>
    <LayoutWithContainer>
        <div v-if="$wait.is('fetching preview resource')" class="loading">
            <Spinner :height="40" :width="40"/>
        </div>
        <template v-else-if="resource">
            <template slot="header" v-if="resource.event">
                <ContentHeader
                    :title="resource.name"
                    :back-link="`/documents/conferences/${resource.event.id}`"
                    :back-link-title="`Back to ${resource.event.eventName}`"
                >
                    <template slot="actions">
                        <router-link
                            :to="{ name: 'edit-conference-document', props: { id: id } }"
                            v-if="resource.can.edit"
                            class="btn"
                        >
                            Edit
                        </router-link>
                    </template>
                    <template slot="extra" v-if="resource.attachment && resource.attachment.filename !== 'invalid'">
                        <div class="extra">
                            <span class="filename">{{ resource.attachment.raw.filename }}</span>
                            <DataList
                                :items="[
                                    {
                                        title: 'Last Updated',
                                        value: resource.updatedAt,
                                        icon: 'calendar',
                                        isSector: false,
                                    },
                                    {
                                        title: 'Conference',
                                        value: resource.event.eventName,
                                        icon: 'location',
                                        isSector: false,
                                    },
                                ]"
                            />
                        </div>
                    </template>
                </ContentHeader>
            </template>
            <aside id="filters" v-if="possibleSectors" class="form">
                <div class="form__group" style="max-width: 30rem;">
                    <select name="filter" v-model="filter" class="form__control">
                        <option value="all">All</option>
                        <option
                            v-for="(name, id) in possibleSectors"
                            :id='`filter-${id}`'
                            :value="id"
                        >
                            {{ name }}
                        </option>
                    </select>
                    <label for="filter" class="form__label">Filter by sector:</label>
                    <a
                        href="#"
                        @click.prevent.stop="() => filter = 'all'"
                        class="form__clear"
                        v-if="filter && filter !== 'all'"
                    >
                        Clear
                    </a>
                </div>
            </aside>
            <section v-if="standoutDocuments && standoutDocuments.length > 0">
                <h3 class="h2">Key Stand out Transactions</h3>
                <div class="grid grid--cards">
                    <div class="col-sm-6" v-for="item in standoutDocuments">
                        <StandoutCard :item="item"/>
                    </div>
                </div>
            </section>
            <section v-if="activeProjectsMandates && activeProjectsMandates.length > 0">
                <h3 class="h2">Active projects/mandates</h3>
                <div class="grid grid--cards">
                    <div class="col-sm-6" v-for="item in activeProjectsMandates">
                        <GenericDocumentCard :item="item"/>
                    </div>
                </div>
            </section>
            <section v-if="activeProspectsOpportunities && activeProspectsOpportunities.length > 0">
                <h3 class="h2">Active prospects/opportunities</h3>
                <div class="grid grid--cards">
                    <div class="col-sm-6" v-for="item in activeProspectsOpportunities">
                        <GenericDocumentCard :item="item"/>
                    </div>
                </div>
            </section>
            <section v-if="activeProspectsOpportunities && potentialMAMethodTargets.length > 0">
                <h3 class="h2">Potential MA Method Targets</h3>
                <div class="grid grid--cards">
                    <div class="col-sm-6" v-for="item in potentialMAMethodTargets">
                        <GenericDocumentCard :item="item"/>
                    </div>
                </div>
            </section>
            <section v-if="resource.status !== 2" class="create">
                <button
                    @click="showDeleteModal()"
                    class="btn btn--danger"
                    v-if="resource.can.delete"
                    v-wait:disabled="['saving preview resource', 'deleting preview resource']"
                >
                    Delete Document
                </button>
                <button
                    @click="submitDocument"
                    v-if="resource.can.approve"
                    :to="{name: 'create-event-document', props: { event_id: id }}"
                    class="btn btn--primary"
                >
                    Submit Document
                </button>
            </section>
        </template>
        <div v-else class="error">
            <span class="h2">Unable to load document.</span>
            <button class="btn btn--link" @click.prevent="fetchResource">Retry</button>
        </div>
    </LayoutWithContainer>
</template>
<script>
import API from '@api';
import LayoutBase from '@layouts/LayoutBase.vue';
import ResourcePreview from '@components/ResourcePreview';
import SvgIcon from '@components/SvgIcon.vue';
import ListGenericDocuments from '@components/Documents/Conferences/ListGenericDocuments';
import ListStandoutDocuments from '@components/Documents/Conferences/ListStandoutDocuments';
import ContentHeader from '@components/Header/ContentHeader.vue';
import LayoutWithContainer from '@layouts/LayoutWithContainer.vue';
import Spinner from '@components/Spinner.vue';
import DataList from '@components/DataList/List.vue';
import StandoutCard from '@components/Card/StandoutCard.vue';
import GenericDocumentCard from '@components/Card/GenericDocumentCard.vue';

export default {
    name: 'ConferenceDocumentList',
    mixins: [ResourcePreview],
    components: {
        GenericDocumentCard,
        StandoutCard,
        LayoutWithContainer,
        DataList,
        Spinner,
        ContentHeader,
        ListStandoutDocuments,
        ListGenericDocuments,
        SvgIcon,
        LayoutBase,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
        event_id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            filter: 'all',
            resource: {
                name: '',
                member: '',
                attachment: {
                    filename: '',
                    filesize: null,
                },
                genericDocuments: [],
                standoutDocuments: [],
                can: {
                    edit: false,
                    approve: false,
                    delete: false,
                    submit: false,
                },
            },
            title: 'test',
            deleteModal: {
                title: 'Are you sure you want to delete this Document?',
                buttonLabel: 'Delete Document',
            },
        }
    },
    methods: {
        async fetchResource() {
            try {
                const data = await API.getDocument(this.event_id, this.id);
                console.log({data});
                return data;
            } catch(err) {
                console.warn(err);
            } finally {
                this.$wait.end('fetching preview resource');
            }
        },
        submitDocument() {
            API.submitDocument(this.id)
                .then((res) => {
                    this.$router.push({
                        name: 'document-list',
                        params: {event_id: this.resource.event_id},
                    });
                });
        },
        deleteResource() {
            return API.deleteDocument(this.id).then(() => {
                this.$router.push({name: 'documents'});
            });
        },
    },
    computed: {
        possibleSectors() {
            const possSectorIds = {};

            ([...this.resource.standoutDocuments ?? [], ...this.resource.genericDocuments ?? []])
                .forEach(item => {
                    if(item.sector_id) {
                        possSectorIds[item.sector_id] = item.sector;
                    }
                })

            return possSectorIds;
        },
        standoutDocuments() {
            let returnedValue = [...(this.resource.standoutDocuments ?? [])];

            if(this.filter && this.filter !== "all") {
                returnedValue = returnedValue
                    .filter((value) => parseFloat(value.sector_id ?? 0) === parseFloat(this.filter));
            }

            return returnedValue;
        },
        activeProjectsMandates() {
            let returnedValue = [...(this.resource.genericDocuments ?? [])]
                .filter((document) => document.type === 0);

            if(this.filter && this.filter !== "all") {
                returnedValue = returnedValue
                    .filter((value) => parseFloat(value.sector_id ?? 0) === parseFloat(this.filter));
            }

            return returnedValue;
        },
        activeProspectsOpportunities() {
            let returnedValue = [...(this.resource.genericDocuments ?? [])]
                .filter((document) => document.type === 1);

            if(this.filter && this.filter !== "all") {
                returnedValue = returnedValue
                    .filter((value) => parseFloat(value.sector_id ?? 0) === parseFloat(this.filter));
            }

            return returnedValue;
        },
        potentialMAMethodTargets() {
            let returnedValue = [...(this.resource.genericDocuments ?? [])]
                .filter((document) => document.type === 2);

            if(this.filter && this.filter !== "all") {
                returnedValue = returnedValue
                    .filter((value) => parseFloat(value.sector_id ?? 0) === parseFloat(this.filter));
            }

            return returnedValue;
        },
    },
}
</script>
<style scoped lang="scss">
@import '~@/theme/variables';

#filters {
    padding-bottom: 3.2rem;
    margin-bottom: 3rem;
    border-bottom: 1px solid #CFCFCF;

    #filters__title {
        position: relative;
        display: block;
        font-size: 1.5rem;
        line-height: 2.3rem;
        font-weight: 500;
        margin-bottom: .5rem;
    }
}

.document-preview {
    .toolbar {
        display: none;
    }
}

.create {
    display: flex;
    justify-content: center;
}

.extra {
    padding-top: 1rem;

    .filename {
        display: block;
        margin-bottom: 3rem;
        color: $text;
        font-size: 1.6rem;
        line-height: 2.2rem;
    }
}

section {
    .h2 {
        margin-bottom: 3rem;
    }

    + section {
        margin-top: 5rem;
        padding-top: 3rem;
        border-top: .1rem solid $border;
    }
}

.error,
.loading {
    min-height: 20rem;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: 1rem;
}
</style>
